import { useState, useEffect } from "react";
import { useApp } from "@/context/AppContext";
import { Shield, Coins, Tag, Search, Check, X, RefreshCw, Plus, Clock, ExternalLink, Activity } from "lucide-react";

type Offer = {
  id: string;
  creatorUserId: string;
  tokenMint: string;
  amount: number;
  price: number;
  status: string;
  createdAt: number;
  expiresAt?: number;
};

type Escrow = {
  id: string;
  offerId: string;
  buyerUserId: string;
  sellerUserId: string;
  amount: number;
  price: number;
  status: string; // "pending" | "released" | "cancelled"
  createdAt: number;
  txSignature?: string;
  releasedAt?: number;
  cancelledAt?: number;
};

export function P2pEscrowPanel() {
  const [activeTab, setActiveTab] = useState<"marketplace" | "my-escrows">("marketplace");
  const { addAppToast } = useApp() as any;
  const [offers, setOffers] = useState<Offer[]>([]);
  const [escrows, setEscrows] = useState<Escrow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchOffers = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/p2p/offers");
      const data = await res.json();
      if (data.success) {
        setOffers(data.data);
      } else {
        setError(data.error || "Failed to fetch offers");
      }
    } catch (e: any) {
      setError(e.message || "Failed to fetch offers");
    } finally {
      setLoading(false);
    }
  };

  const fetchEscrows = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/p2p/escrows");
      const data = await res.json();
      if (data.success) {
        setEscrows(data.data);
      } else {
        setError(data.error || "Failed to fetch escrows");
      }
    } catch (e: any) {
      setError(e.message || "Failed to fetch escrows");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "marketplace") {
      fetchOffers();
    } else {
      fetchEscrows();
    }
  }, [activeTab]);

  const acceptOffer = async (id: string) => {
    try {
      const res = await fetch(`/api/p2p/offers/${id}/accept`, {
        method: "POST",
      });
      const data = await res.json();
      if (data.success) {
        // Refresh offers
        fetchOffers();
      } else {
        addAppToast(data.error || "Failed to accept offer", "error");
      }
    } catch (e: any) {
      addAppToast(e.message || "Failed to accept offer", "error");
    }
  };

  const releaseEscrow = async (id: string) => {
    try {
      const res = await fetch(`/api/p2p/escrows/${id}/release`, {
        method: "POST",
      });
      const data = await res.json();
      if (data.success) {
        fetchEscrows();
      } else {
        addAppToast(data.error || "Failed to release escrow", "error");
      }
    } catch (e: any) {
      addAppToast(e.message || "Failed to release escrow", "error");
    }
  };

  const cancelEscrow = async (id: string) => {
    try {
      const res = await fetch(`/api/p2p/escrows/${id}/cancel`, {
        method: "POST",
      });
      const data = await res.json();
      if (data.success) {
        fetchEscrows();
      } else {
        addAppToast(data.error || "Failed to cancel escrow", "error");
      }
    } catch (e: any) {
      addAppToast(e.message || "Failed to cancel escrow", "error");
    }
  };

  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-black via-zinc-950 to-black text-white overflow-hidden p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-500/10 border border-emerald-500/20 rounded-xl shadow-[0_0_15px_rgba(16,185,129,0.15)]">
            <Shield className="size-5 text-emerald-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold tracking-wide text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 to-emerald-500">
              P2P Escrow
            </h2>
            <p className="text-[10px] text-white/40 uppercase tracking-widest font-semibold mt-0.5">Secure Trustless Trading</p>
          </div>
        </div>
        <div className="flex bg-white/5 p-1 rounded-lg border border-white/10 backdrop-blur-md">
          <button
            onClick={() => setActiveTab("marketplace")}
            className={`px-4 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-wider transition-all duration-300 ${
              activeTab === "marketplace"
                ? "bg-emerald-500 text-black shadow-[0_0_10px_rgba(16,185,129,0.5)]"
                : "text-white/40 hover:text-white/80"
            }`}
          >
            Marketplace
          </button>
          <button
            onClick={() => setActiveTab("my-escrows")}
            className={`px-4 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-wider transition-all duration-300 ${
              activeTab === "my-escrows"
                ? "bg-emerald-500 text-black shadow-[0_0_10px_rgba(16,185,129,0.5)]"
                : "text-white/40 hover:text-white/80"
            }`}
          >
            Yours
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto no-scrollbar relative rounded-xl border border-white/5 bg-white/[0.02] backdrop-blur-3xl shadow-inner">
        {loading && (
          <div className="absolute inset-0 z-10 bg-black/40 backdrop-blur-sm flex items-center justify-center">
            <RefreshCw className="size-6 text-emerald-400 animate-spin" />
          </div>
        )}

        {error && (
          <div className="m-4 p-3 rounded-lg bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs font-medium">
            {error}
          </div>
        )}

        {activeTab === "marketplace" && (
          <div className="p-4 space-y-4">
            <div className="flex justify-between items-center pb-2 border-b border-white/10">
              <h3 className="text-sm font-bold text-white/80">Active Offers</h3>
              <button className="flex items-center gap-1 px-3 py-1.5 bg-emerald-500/20 border border-emerald-500/30 hover:bg-emerald-500/30 text-emerald-400 rounded-lg text-xs font-bold uppercase tracking-wider transition-all shadow-[0_0_10px_rgba(16,185,129,0.1)] hover:shadow-[0_0_15px_rgba(16,185,129,0.3)]">
                <Plus className="size-3.5" /> New Offer
              </button>
            </div>

            {offers.length === 0 && !loading && (
              <div className="flex flex-col items-center justify-center py-12 text-white/30">
                <Search className="size-8 mb-3 opacity-50" />
                <p className="text-sm font-medium">No active offers available</p>
                <p className="text-xs mt-1">Be the first to create a new P2P offer.</p>
              </div>
            )}

            <div className="grid grid-cols-1 gap-3">
              {offers.map((offer) => (
                <div key={offer.id} className="p-4 rounded-xl bg-white/5 border border-white/10 hover:border-emerald-500/30 transition-colors group relative overflow-hidden">
                  <div className="absolute top-0 right-0 p-2 opacity-10 group-hover:opacity-20 transition-opacity">
                    <Shield className="size-20" />
                  </div>
                  <div className="flex justify-between items-start relative z-10">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Tag className="size-3.5 text-emerald-400" />
                        <span className="text-xs font-mono text-emerald-400 font-bold">{offer.tokenMint.slice(0, 8)}...</span>
                      </div>
                      <div className="flex items-baseline gap-1.5 mt-2">
                        <span className="text-2xl font-black text-white tracking-tight">{offer.amount.toLocaleString()}</span>
                        <span className="text-xs font-bold text-white/40 uppercase">Tokens</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-white/40 mb-1">Price</div>
                      <div className="flex items-center gap-1 justify-end">
                        <Coins className="size-3.5 text-sky-400" />
                        <span className="text-lg font-bold text-sky-400">{offer.price} SOL</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-3 border-t border-white/10 flex justify-between items-center relative z-10">
                    <span className="text-[10px] text-white/30 font-medium">
                      By: {offer.creatorUserId.slice(0,6)}...
                    </span>
                    <button 
                      onClick={() => acceptOffer(offer.id)}
                      className="px-4 py-1.5 rounded-lg bg-emerald-500 text-black text-xs font-bold uppercase tracking-wider hover:bg-emerald-400 transition-colors shadow-[0_0_10px_rgba(16,185,129,0.3)]"
                    >
                      Accept Trade
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === "my-escrows" && (
          <div className="p-4 space-y-4">
            <h3 className="text-sm font-bold text-white/80 pb-2 border-b border-white/10">Active & Completed Escrows</h3>
            
            {escrows.length === 0 && !loading && (
              <div className="flex flex-col items-center justify-center py-12 text-white/30">
                <Activity className="size-8 mb-3 opacity-50" />
                <p className="text-sm font-medium">No escrows found</p>
                <p className="text-xs mt-1">You haven't participated in any P2P trades yet.</p>
              </div>
            )}

            <div className="grid grid-cols-1 gap-3">
              {escrows.map((escrow) => (
                <div key={escrow.id} className="p-4 rounded-xl bg-white/5 border border-white/10 relative overflow-hidden">
                  <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-2">
                      <Clock className="size-4 text-white/40" />
                      <span className="text-xs font-bold text-white/60">{new Date(escrow.createdAt).toLocaleDateString()}</span>
                    </div>
                    <span className={`px-2 py-1 rounded text-[9px] font-bold uppercase tracking-wider border ${
                      escrow.status === "pending" ? "bg-amber-500/10 text-amber-400 border-amber-500/20" :
                      escrow.status === "released" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" :
                      "bg-rose-500/10 text-rose-400 border-rose-500/20"
                    }`}>
                      {escrow.status}
                    </span>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-black/40 rounded-lg border border-white/5 mb-3">
                    <div>
                      <span className="block text-[10px] text-white/40 font-bold uppercase tracking-wider mb-0.5">Amount</span>
                      <span className="text-sm font-bold">{escrow.amount.toLocaleString()}</span>
                    </div>
                    <div className="text-right">
                      <span className="block text-[10px] text-white/40 font-bold uppercase tracking-wider mb-0.5">Price (SOL)</span>
                      <span className="text-sm font-bold text-sky-400">{escrow.price}</span>
                    </div>
                  </div>

                  {escrow.status === "pending" && (
                    <div className="flex gap-2 justify-end mt-2">
                      <button 
                        onClick={() => cancelEscrow(escrow.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 rounded-lg text-[10px] font-bold uppercase tracking-wider border border-rose-500/20 transition-all"
                      >
                        <X className="size-3" /> Cancel
                      </button>
                      <button 
                        onClick={() => releaseEscrow(escrow.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 rounded-lg text-[10px] font-bold uppercase tracking-wider border border-emerald-500/20 transition-all"
                      >
                        <Check className="size-3" /> Release Funds
                      </button>
                    </div>
                  )}
                  {escrow.txSignature && (
                    <div className="mt-3 text-right">
                      <a href={`https://solscan.io/tx/${escrow.txSignature}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[10px] text-sky-400 hover:text-sky-300 font-medium">
                        View Tx <ExternalLink className="size-2.5" />
                      </a>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
